import type { SubscriptionItemListItemDTO } from '@kanbu/schema/contracts';
import {
  BillingInterval,
  type Currency,
  SubscriptionStatus,
} from '@kanbu/schema/enums';
import { formatUtils } from '@kanbu/shared';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button } from '@utima/ui';
import {
  Banknote,
  CalendarDays,
  CalendarX,
  CreditCard,
  Info,
  RefreshCw,
  RotateCcw,
} from 'lucide-react';
import { type ReactNode, useMemo } from 'react';

import { Alert } from '@/components/Alert';
import { Card } from '@/components/card/Card';

import type { BillingPeriodKey } from './billingPeriods';
import type { CollectionMethodTarget } from './planManagementShared';
import { StatusBadge } from './StatusBadge';

type CurrentSubscriptionCardProps = {
  tierName?: string;
  status?: SubscriptionStatus | string;
  billingInterval?: BillingInterval | null;
  periodStart?: Date | string | null;
  periodEnd?: Date | string | null;
  paymentMethod?: CollectionMethodTarget | null;
  items: SubscriptionItemListItemDTO[];
  isLoading: boolean;
  /** Whether an end-of-term cancellation is already scheduled. */
  cancelsAtPeriodEnd: boolean;
  onCancelAtPeriodEnd: () => void;
  onResume: () => void;
  isCancelActionPending: boolean;
};

function formatPeriodDate(value?: Date | string | null): string {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function statusTone(
  status?: SubscriptionStatus | string,
): 'success' | 'warning' | 'danger' | 'default' {
  if (status === SubscriptionStatus.Active) {
    return 'success';
  }

  if (status === SubscriptionStatus.PastDue) {
    return 'warning';
  }

  if (
    status === SubscriptionStatus.Canceled ||
    status === SubscriptionStatus.Unpaid
  ) {
    return 'danger';
  }

  return 'default';
}

function periodKeyFromInterval(
  interval: BillingInterval,
  intervalCount: number,
): BillingPeriodKey {
  if (interval === BillingInterval.Year) {
    return 'year';
  }

  if (intervalCount === 3) {
    return 'quarter';
  }

  if (intervalCount === 6) {
    return 'half_year';
  }

  return 'month';
}

function SummaryField({
  icon,
  label,
  children,
  hint,
}: {
  icon?: ReactNode;
  label: string;
  children: ReactNode;
  hint?: ReactNode;
}) {
  return (
    <div className='space-y-1'>
      <p className='text-muted-foreground flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide'>
        {icon}
        {label}
      </p>
      <div className='text-sm'>{children}</div>
      {hint && <p className='text-muted-foreground text-xs'>{hint}</p>}
    </div>
  );
}

export function CurrentSubscriptionCard({
  tierName,
  status,
  billingInterval,
  periodStart,
  periodEnd,
  paymentMethod,
  items,
  isLoading,
  cancelsAtPeriodEnd,
  onCancelAtPeriodEnd,
  onResume,
  isCancelActionPending,
}: CurrentSubscriptionCardProps) {
  const { t } = useLingui();

  const statusLabel = useMemo(() => {
    switch (status) {
      case SubscriptionStatus.Active:
        return t`Active`;
      case SubscriptionStatus.Canceled:
        return t`Canceled`;
      case SubscriptionStatus.PastDue:
        return t`Past due`;
      case SubscriptionStatus.Unpaid:
        return t`Unpaid`;
      case SubscriptionStatus.Incomplete:
        return t`Incomplete`;
      case SubscriptionStatus.IncompleteExpired:
        return t`Incomplete expired`;
      case SubscriptionStatus.Trialing:
        return t`Trialing`;

      default:
        return status ? String(status) : t`Unknown`;
    }
  }, [status, t]);

  const billingPeriodLabels = useMemo(
    (): Record<BillingPeriodKey, string> => ({
      month: t`Monthly`,
      quarter: t`Quarterly`,
      half_year: t`Half-yearly`,
      year: t`Yearly`,
    }),
    [t],
  );

  const billingPeriodShortLabels = useMemo(
    (): Record<BillingPeriodKey, string> => ({
      month: t`mo`,
      quarter: t`qtr`,
      half_year: t`6 mo`,
      year: t`yr`,
    }),
    [t],
  );

  const hasMixedItemPeriods =
    new Set(items.map(item => `${item.interval}:${item.intervalCount}`)).size >
    1;

  const billingPeriodLabel = useMemo(() => {
    if (hasMixedItemPeriods) {
      return t`Mixed periods`;
    }

    if (!billingInterval) {
      return '—';
    }

    return billingInterval === BillingInterval.Year ? t`Yearly` : t`Monthly`;
  }, [billingInterval, hasMixedItemPeriods, t]);

  // Gate on a paid tier, not on `status`: a canceled org falls back to an
  // active Free subscription, which would otherwise render a half-empty
  // "Active" card with stale period/payment data.
  const hasPaidSubscription = Boolean(tierName);

  return (
    <Card title={t`Current subscription`} className='h-full'>
      {isLoading && (
        <p className='text-muted-foreground text-sm'>
          <Trans>Loading subscription...</Trans>
        </p>
      )}

      {!isLoading && !hasPaidSubscription && (
        <div className='flex flex-wrap items-center gap-x-3 gap-y-2'>
          <StatusBadge label={t`Free plan`} tone='default' />
          <span className='text-muted-foreground text-sm'>
            <Trans>No active paid subscription. Assign a plan below.</Trans>
          </span>
        </div>
      )}

      {!isLoading && hasPaidSubscription && (
        <div className='space-y-5'>
          <div className='flex flex-wrap items-center gap-x-3 gap-y-2'>
            {tierName && (
              <span className='text-lg font-semibold leading-none'>
                {tierName}
              </span>
            )}
            <StatusBadge label={statusLabel} tone={statusTone(status)} />
            <span className='text-muted-foreground rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium'>
              {billingPeriodLabel}
            </span>
          </div>

          <div className='grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-3'>
            <SummaryField
              icon={<CalendarDays className='size-3.5' />}
              label={t`Current period`}
            >
              {periodStart || periodEnd
                ? `${formatPeriodDate(periodStart)} – ${formatPeriodDate(periodEnd)}`
                : '—'}
            </SummaryField>

            <SummaryField
              icon={<RefreshCw className='size-3.5' />}
              label={t`Next renewal`}
            >
              <span className='font-medium'>{formatPeriodDate(periodEnd)}</span>
            </SummaryField>

            <SummaryField
              icon={
                paymentMethod === 'bank_transfer' ? (
                  <Banknote className='size-3.5' />
                ) : (
                  <CreditCard className='size-3.5' />
                )
              }
              label={t`Payment method`}
            >
              {paymentMethod === 'bank_transfer' ? (
                <Trans>Bank transfer</Trans>
              ) : paymentMethod === 'card' ? (
                <Trans>Card</Trans>
              ) : (
                <span className='text-muted-foreground'>—</span>
              )}
            </SummaryField>
          </div>

          {items.length > 0 && (
            <div className='overflow-x-auto rounded-md border'>
              <table className='w-full text-left text-sm'>
                <thead>
                  <tr className='text-muted-foreground border-b bg-muted/40 text-xs uppercase tracking-wide'>
                    <th className='px-3 py-2 font-medium'>
                      <Trans>Item</Trans>
                    </th>
                    <th className='px-3 py-2 font-medium'>
                      <Trans>Type</Trans>
                    </th>
                    <th className='px-3 py-2 font-medium'>
                      <Trans>Period</Trans>
                    </th>
                    <th className='px-3 py-2 text-right font-medium'>
                      <Trans>Price</Trans>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => {
                    const periodKey = periodKeyFromInterval(
                      item.interval as BillingInterval,
                      item.intervalCount,
                    );

                    return (
                      <tr key={item.id} className='border-b last:border-b-0'>
                        <td className='px-3 py-2 font-medium'>
                          {item.productName}
                        </td>
                        <td className='px-3 py-2'>
                          <StatusBadge
                            label={
                              item.productType === 'addon' ? t`Add-on` : t`Plan`
                            }
                            tone={
                              item.productType === 'addon' ? 'info' : 'default'
                            }
                          />
                        </td>
                        <td className='text-muted-foreground px-3 py-2'>
                          {billingPeriodLabels[periodKey]}
                        </td>
                        <td className='px-3 py-2 text-right tabular-nums'>
                          {formatUtils.currency(
                            item.unitAmount,
                            item.currency as Currency,
                          )}{' '}
                          <span className='text-muted-foreground'>
                            / {billingPeriodShortLabels[periodKey]}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className='text-muted-foreground flex items-start gap-2 border-t pt-3 text-xs'>
            <Info className='mt-0.5 size-3.5 shrink-0' />
            <span>
              {hasMixedItemPeriods && (
                <>
                  <Trans>
                    Items renew on different schedules; Stripe invoices them
                    together when periods align.
                  </Trans>{' '}
                </>
              )}
              <Trans>
                An unpaid invoice on any line item can cancel the whole
                subscription. Add-on changes are made in the Stripe Dashboard.
              </Trans>
            </span>
          </div>

          <div className='space-y-3 rounded-md border border-red-500/30 bg-red-500/10 p-4'>
            <h3 className='text-sm font-medium'>
              <Trans>Cancel subscription</Trans>
            </h3>
            {cancelsAtPeriodEnd ? (
              <div className='space-y-4'>
                <Alert
                  variant='warning'
                  title={<Trans>Cancellation scheduled</Trans>}
                  description={
                    <Trans>
                      This subscription is set to cancel on{' '}
                      {formatPeriodDate(periodEnd)}. Service continues until
                      then, after which the plan reverts to Free. You can still
                      resume it before that date.
                    </Trans>
                  }
                />
                <Button
                  variant='secondary'
                  size='sm'
                  className='self-start'
                  loading={isCancelActionPending}
                  onClick={onResume}
                >
                  <RotateCcw className='mr-1.5 size-4' />
                  <Trans>Resume subscription</Trans>
                </Button>
              </div>
            ) : (
              <div className='space-y-3'>
                <p className='text-muted-foreground text-sm'>
                  <Trans>
                    Schedule cancellation at the end of the current paid period.
                    The customer keeps access until{' '}
                    {formatPeriodDate(periodEnd)}, then the plan reverts to
                    Free. Any unused allowance is credited to the balance.
                    Reversible until that date.
                  </Trans>
                </p>
                <Button
                  variant='ghost'
                  size='sm'
                  className='self-start text-red-600 hover:bg-red-50 hover:text-red-700'
                  loading={isCancelActionPending}
                  onClick={onCancelAtPeriodEnd}
                >
                  <CalendarX className='mr-1.5 size-4' />
                  <Trans>Cancel at end of term</Trans>
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
